import React, { useMemo, useContext } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var ColorPalette = {
    PureWhite: '#FFFFFF',
    Grey_100: '#121213',
    Grey_90: '#262627',
    Grey_80: '#3E3E40',
    Grey_5: '#E3E3E6',
    Grey_1: '#F2F2F5',
    Grey_0: '#F5F5F8',
    Grape_100: '#2B2457',
    Grape_80: '#413B6B',
    Grape_60: '#676285',
    Grape_40: '#9E9BB0',
    Stardust_100: '#E4E3E8',
    Stardust_80: '#C7C5CE',
    Stardust_60: '#A3A2AB',
    Stardust_40: '#74737A',
    Indigo_100: '#2E2266',
    Indigo_90: '#3D308E',
    Indigo_80: '#4A3EB3',
    Indigo_60: '#635BE3',
    Indigo_40: '#928FF7',
    Indigo_10: '#D4D4FC',
    Indigo_5: '#E8E8FF',
    Apricot_20: '#FFD5AF',
    Mint_70: '#038568',
    Mint_50: '#14A37D',
    Mint_30: '#5AE0B4',
    Mint_10: '#CBFCE8',
    Pink_50: '#DB373C',
    Pink_30: '#F2666F',
};

var Categories = {
    category: {
        cash: '#FFA216',
        entertainment: '#FF5E5E',
        groceries: '#B557FF',
        health: '#2BD07C',
        homeandhousehold: '#FF4646',
        inandout: '#674EFF',
        insurance: '#2CC5BB',
        lifestyle: '#FF54A6',
        other: '#939393',
        peer2peer: '#9CBE12',
        restaurant: '#F15396',
        services: '#00AACF',
        shopping: '#FF8E5E',
        trading: '#54BD4B',
        transfers: '#FFB268',
        transport: '#587DFF',
        travel: '#F4C20F',
    },
};
var Light = __assign({ name: 'Light', brand: {
        main: '#8940F7' /* OLD */,
        secondary: ColorPalette.Apricot_20 /* OLD */,
        indigo: '#8940F7',
        indigo2: '#8940F7',
        apricot: '#FFD5AF',
        orange: '#FF865C',
    }, general: {
        white: ColorPalette.PureWhite,
        black: '#000000' /* OLD */,
        border: '#DCDCFF',
    }, glyph: {
        main: ColorPalette.Grey_100,
        branded: '#332C6B',
    }, clickable: {
        text: { orange: '#FF865C', purple: '#5C33EC' },
        BG: '#8940F7',
        inactive: ColorPalette.Grape_40,
        main: '#3E51FF', // OLD
        secondary: '#4E5FFF', // OLD
        important: '#FF447C', // OLD
        complementary: '#A9FFD6', // OLD
    }, text: {
        headline: {
            small: '#332C6B',
            big: '#5237BF',
        },
        main: ColorPalette.Grey_100,
        sub1: '#2D2761',
        sub2: '#736E7A',
        sub3: '#7A75A4',
        sub4: '#8183B9',
        section: {
            main: '#505A87', // OLD
            secondary: '#FF584B', // OLD
        },
        extra: {
            darkGreen: '#007256', // OLD
        },
    }, positive: {
        text: ColorPalette.Mint_70,
        fill: '#23C494',
    }, negative: {
        text: '#F33053',
    }, background: {
        screen: '#F9FBFF',
        surface: ColorPalette.PureWhite,
        sub: ColorPalette.Grey_0,
        complementary: '#F1F4FF',
        loading: '#ECECFF',
        main: '#A9FFD6', // OLD
        secondary: '#CDFFE7', // OLD
        extra: '#DFE2EA', // OLD
        onboarding: '#343152',
        darksurface: '#1D1B2F',
        banner: '#E9E6FF',
    }, menu: { border: '#F5F5FF', icon: '#38355A' }, form: {
        search: {
            BG: '#F5F5F8',
        },
        input: {
            placeholder: '#8940F7',
            active: '#8940F7',
            BG: '#F5F5F8',
            focus: '#ECECFF',
            focusColor: '#8940F7',
            color: '#494283',
            border: '#BBB7D1',
        },
        checkbox: {
            color: '#7A75A4',
            checkedColor: '#23C494',
            disabled: '#BBB7D1',
        },
    }, switch: { BG: '#F2F2F5', text: { selected: '#2D2761' } }, product: { BG: '#0E0D16', text: '#F2F4FF' }, contact: {
        rocker: { BG: '#8940F7', text: '#FFFFFF' },
        nonRocker: { BG: '#DBDBDB', text: '#5B5B5B' },
    }, button: {
        main: {
            BG: '#8940F7',
            border: '#8940F7',
            text: '#FFFFFF',
            active: '#8940F7',
            hover: '#8940F7',
            disabled: '#968EB4',
        },
        secondary: {
            outlined: {
                border: '#8940F7',
                text: '#8940F7',
                disabled: '#968EB4',
            },
            filled: {
                BG: '#8940F7',
                border: '#8940F7',
                text: '#FFFFFF',
            },
        },
    }, stepper: { default: '#BBB7D1', active: '#8940F7', complete: '#20A87F' } }, Categories);
var Dark = __assign({ name: 'Dark', brand: {
        main: '#8940F7' /* OLD */,
        secondary: ColorPalette.Apricot_20 /* OLD */,
        indigo: '#8940F7',
        indigo2: '#8940F7',
        apricot: '#FFD5AF',
        orange: '#FF865C',
    }, general: {
        white: ColorPalette.PureWhite,
        black: '#000000' /* OLD */,
        border: '#1D1B2F',
    }, glyph: {
        main: ColorPalette.Grey_0,
        branded: ColorPalette.Indigo_10,
    }, clickable: {
        text: { orange: '#FF865C', purple: ColorPalette.Indigo_40 },
        BG: '#8940F7',
        inactive: ColorPalette.Stardust_40,
        main: '#3E51FF', // OLD
        secondary: '#4E5FFF', // OLD
        important: '#FF447C', // OLD
        complementary: '#A9FFD6', // OLD
    }, text: {
        headline: {
            small: '#C8C1FF',
            big: '#7E5FFF',
        },
        main: '#F2F4FF',
        sub1: '#E2E7FF',
        sub2: '#C4CDF5',
        sub3: '#99A6E1',
        sub4: '#B0BDF4',
        section: {
            main: '#505A87', // OLD
            secondary: '#FF584B', // OLD
        },
        extra: {
            darkGreen: '#007256', // OLD
        },
    }, positive: {
        text: ColorPalette.Mint_30,
        fill: ColorPalette.Mint_30,
    }, negative: {
        text: ColorPalette.Pink_30,
    }, background: {
        screen: '#0E0D16',
        surface: '#1D1B2F',
        sub: ColorPalette.Grey_80,
        complementary: '#F1F4FF',
        loading: '#8B87AC',
        main: '#A9FFD6', // OLD
        secondary: '#CDFFE7', // OLD
        extra: '#DFE2EA', // OLD
        onboarding: '#343152',
        darksurface: '#1D1B2F',
        banner: '#38355A',
    }, menu: { border: '#312E4E', icon: '#C2C5E0' }, form: {
        search: { BG: '#1D1B2F' },
        input: {
            placeholder: '#928FF7',
            active: '#928FF7',
            BG: '#F5F5F8',
            focus: '#ECECFF',
            focusColor: '#8940F7',
            color: '#8940F7',
            border: '#BBB7D1',
        },
        checkbox: {
            color: '#7A75A4',
            checkedColor: '#23C494',
            disabled: '#BBB7D1',
        },
    }, switch: { BG: '#1D1B2F', text: { selected: '#2D2761' } }, product: { BG: '#0E0D16', text: '#F2F4FF' }, contact: {
        rocker: { BG: '#8940F7', text: '#FFFFFF' },
        nonRocker: { BG: '#DBDBDB', text: '#5B5B5B' },
    }, button: {
        main: {
            BG: '#8940F7',
            border: '#8940F7',
            text: '#FFFFFF',
            active: '#8940F7',
            hover: '#8940F7',
            disabled: '#968EB4',
        },
        secondary: {
            outlined: {
                border: '#8940F7',
                text: '#8940F7',
                disabled: '#968EB4',
            },
            filled: {
                BG: '#8940F7',
                border: '#8940F7',
                text: '#FFFFFF',
            },
        },
    }, stepper: { default: '#BBB7D1', active: '#8940F7', complete: '#20A87F' } }, Categories);

var ColorScheme = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Dark: Dark,
    Light: Light
});

var ObjektivMk2 = function (platform) { return ({
    regular: platform === 'android'
        ? 'ObjektivMk2_Rg'
        : platform === 'ios'
            ? 'ObjektivMk2-Regular'
            : 'AeonikPro-Regular',
    bold: platform === 'android'
        ? 'ObjektivMk2_Bd'
        : platform === 'ios'
            ? 'ObjektivMk2-Bold'
            : 'AeonikPro-Bold',
    xbold: 'AeonikPro-Bold',
}); };
// New fonts since v2.X.X
var AeonikPro$1 = {
    regular: 'Aeonik-Regular',
    medium: 'AeonikPro-Medium',
    bold: 'AeonikPro-Bold',
    xbold: 'AeonikPro-Bold',
};
var Default = function (platform) { return ({
    headline: platform === 'web' ? ObjektivMk2(platform).bold : AeonikPro$1.bold,
    regular: platform === 'web' ? ObjektivMk2(platform).regular : AeonikPro$1.regular,
    bold: platform === 'web' ? ObjektivMk2(platform).bold : AeonikPro$1.bold,
    xbold: platform === 'web' ? ObjektivMk2(platform).xbold : AeonikPro$1.bold,
}); };

var FontFamily = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Default: Default
});

var theme = {
    fontSize: {
        xxxsmall: 7.5,
        xxsmall: 11,
        xsmall: 12,
        small: 13,
        medium: 16,
        large: 18.5,
        xlarge: 20,
        xxlarge: 27,
        xxxlarge: 34,
    },
    lineHeight: {
        medium: 19,
        xlarge: 29,
    },
    size: {
        xxxsmall: 7,
        xxsmall: 10,
        xsmall: 12,
        small: 12,
        medium: 16,
        large: 18,
        xlarge: 20,
        xxlarge: 22,
        xxxlarge: 26,
    },
    actionButtonSize: {
        xxsmall: 30,
        xsmall: 40,
        small: 50,
        medium: 60,
        large: 70,
        xlarge: 80,
        xxlarge: 90,
    },
    buttonSize: {
        xxsmall: 8,
        xsmall: 10,
        small: 12,
        medium: 14,
        large: 16,
        xlarge: 18,
        xxlarge: 20,
    },
    buttonWidth: {
        small: 88,
        medium: 105,
        large: 126,
        xlarge: 210,
    },
    buttonHeight: {
        small: 28,
        medium: 32,
        large: 40,
    },
    avatarSize: {
        xxsmall: 40,
        xsmall: 60,
        small: 80,
        medium: 90,
        large: 110,
        xlarge: 120,
        xxlarge: 140,
    },
    badgeSize: {
        small: 24,
        medium: 28,
        large: 32,
    },
    miniBadgeSize: {
        xxsmall: 10,
        xsmall: 11,
        small: 12,
        medium: 15,
        large: 17,
        xlarge: 19,
        xxlarge: 21,
    },
    listItemSpace: {
        xxsmall: 5,
        xsmall: 7.5,
        small: 10,
        medium: 12,
        large: 14,
        xlarge: 16,
        xxlarge: 18,
    },
    space: {
        none: 0,
        xxsmall: 2,
        xsmall: 4,
        small: 8,
        medium: 12,
        large: 16,
        xlarge: 20,
        xxlarge: 24,
    },
    layoutSpace: {
        none: 0,
        xxsmall: 5,
        xsmall: 10,
        small: 12,
        medium: 16,
        large: 20,
        xlarge: 28,
        xxlarge: 32,
        xxxlarge: 62,
    },
    contactSize: {
        small: 36,
        large: 46,
    },
    // ROCKER UI
    iconSize: {
        xxsmall: 13,
        xsmall: 19,
        small: 32,
        medium: 36,
        large: 44,
        xlarge: 72,
    },
    iconInnerSize: {
        xxsmall: 7,
        xsmall: 9,
        small: 16,
        medium: 20,
        large: 24,
        xlarge: 41,
    },
    searchSize: {
        height: 38,
    },
    contactColor: {
        white: '#FFFFFF',
        light: '#F1F4FF',
        darkText: '#505A87',
        default: '#242E57',
        lightGreen: '#BDFFC4',
        lightBlue: '#BDE7FF',
        lightPurple: '#E4CFFF',
        lightYellow: '#FFFAD1',
        lightPink: '#FFC0DE',
        lightOrange: '#FFD1B0',
        lightRed: '#FFB1B1',
        lightGrey: '#DBDBDB',
        lightTurquoise: '#C9FFF2',
        greenText1: '#A9FFD6',
        greenText2: '#1CAC67',
        greenText3: '#0A9D7B',
        blueText: '#0193E4',
        purpleText: '#914AEB',
        yellowText: '#F9BB00',
        pinkText: '#C94B88',
        orangeText: '#DE650D',
        redText: '#C64949',
        greyText: '#5B5B5B',
        skeleton: '#D6DDF8',
    },
    shadows: {
        event: {
            elevation: 16,
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.4,
            shadowRadius: 14,
            shadowColor: '#C1CBD7',
        },
        defaultShadow: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.1,
            shadowRadius: 11,
            elevation: 2,
        },
    },
};

var assemble = function (colorScheme, fontFamily) { return ({
    color: colorScheme,
    fontFamily: fontFamily,
    deprecated: theme,
}); };

var Util = /*#__PURE__*/Object.freeze({
    __proto__: null,
    assemble: assemble
});

var Context = React.createContext(undefined);
var useThemeContext = function () {
    var themeContext = React.useContext(Context);
    if (themeContext === undefined) {
        throw new Error('useThemeContext must be used within a ThemeProvider');
    }
    return themeContext;
};
var ThemeProvider = function (props) {
    if (props.value === undefined) {
        throw new Error('theme value must be provided within a ThemeProvider');
    }
    return (React.createElement(Context.Provider, { value: props.value }, props.children));
};

// New fonts since v2.8.0
var AeonikPro = {
    regular: "Aeonik-Regular",
    medium: "AeonikPro-Medium",
    bold: "AeonikPro-Bold",
    xbold: "AeonikPro-Bold",
};

var BASE_SIZE = 16;
var FACTOR = 1.125;
var getSize = function (step) {
    return "".concat(BASE_SIZE * Math.pow(FACTOR, step), "px");
};
var getLineHeight = function (step, scale) {
    return "".concat(BASE_SIZE * Math.pow(FACTOR, step) * scale, "px");
};
var getTypographyForWeight = function (weight) {
    switch (weight) {
        case "400":
            return {
                fontWeight: "400",
                fontFamily: AeonikPro.regular,
            };
        case "500":
            return {
                fontWeight: "500",
                fontFamily: AeonikPro.medium,
            };
        case "700":
            return {
                fontWeight: "700",
                fontFamily: AeonikPro.bold,
            };
        case "800":
            return {
                fontWeight: "800",
                fontFamily: AeonikPro.bold,
            };
    }
};

/** Page titles large. */
var HEADING_1 = __assign({ fontSize: getSize(4), lineHeight: getLineHeight(4, 1.3) }, getTypographyForWeight("700"));
/** Category headlines. */
var HEADING_2 = __assign({ fontSize: getSize(2), lineHeight: getLineHeight(2, 1.3) }, getTypographyForWeight("700"));
/** Paragraph headlines. */
var HEADING_3 = __assign({ fontSize: getSize(1), lineHeight: getLineHeight(1, 1.3) }, getTypographyForWeight("700"));
/** Smaller headlines. */
var HEADING_4 = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.2) }, getTypographyForWeight("500"));
/** List item headlines. */
var HEADING_4_BOLD = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.4) }, getTypographyForWeight("700"));
/** Smallest headlines. */
var HEADING_5 = __assign({ fontSize: getSize(-1), lineHeight: getLineHeight(-1, 1.4) }, getTypographyForWeight("500"));
/** List item headlines. */
var HEADING_5_BOLD = __assign({ fontSize: getSize(-1), lineHeight: getLineHeight(-1, 1.4) }, getTypographyForWeight("700"));

var heading = /*#__PURE__*/Object.freeze({
    __proto__: null,
    HEADING_1: HEADING_1,
    HEADING_2: HEADING_2,
    HEADING_3: HEADING_3,
    HEADING_4: HEADING_4,
    HEADING_4_BOLD: HEADING_4_BOLD,
    HEADING_5: HEADING_5,
    HEADING_5_BOLD: HEADING_5_BOLD
});

/** Main overline text. */
var OVERLINE_1 = __assign(__assign({ fontSize: getSize(-3), lineHeight: getLineHeight(-3, 1.4) }, getTypographyForWeight("800")), { textTransform: "uppercase" });
/** Secondary overline text. */
var OVERLINE_2 = __assign(__assign({ fontSize: getSize(-4), lineHeight: getLineHeight(-4, 1.5) }, getTypographyForWeight("800")), { textTransform: "uppercase" });

var overline = /*#__PURE__*/Object.freeze({
    __proto__: null,
    OVERLINE_1: OVERLINE_1,
    OVERLINE_2: OVERLINE_2
});

/** Primary body text. */
var BODY_1 = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.4) }, getTypographyForWeight("400"));
/** To emphasize parts in the primary body text. */
var BODY_1_EMPHASIS = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.4) }, getTypographyForWeight("500"));
/** Secondary body text. */
var BODY_2 = __assign({ fontSize: getSize(-1), lineHeight: getLineHeight(-1, 1.4) }, getTypographyForWeight("400"));
/** To emphasize parts in the secondary body text. */
var BODY_2_EMPHASIS = __assign({ fontSize: getSize(-1), lineHeight: getLineHeight(-1, 1.4) }, getTypographyForWeight("500"));

var body = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BODY_1: BODY_1,
    BODY_1_EMPHASIS: BODY_1_EMPHASIS,
    BODY_2: BODY_2,
    BODY_2_EMPHASIS: BODY_2_EMPHASIS
});

/** All legal fine print. */
var LEGAL = __assign({ fontSize: getSize(-3), lineHeight: getLineHeight(-3, 1.4) }, getTypographyForWeight("400"));

var meta = /*#__PURE__*/Object.freeze({
    __proto__: null,
    LEGAL: LEGAL
});

/** Main buttons. */
var BUTTON_PRIMARY = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.5) }, getTypographyForWeight("500"));
/** Small buttons, button labels, and text-icon button combinations. */
var BUTTON_SECONDARY = __assign({ fontSize: getSize(-1), lineHeight: getLineHeight(-1, 1.5) }, getTypographyForWeight("500"));

var button = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BUTTON_PRIMARY: BUTTON_PRIMARY,
    BUTTON_SECONDARY: BUTTON_SECONDARY
});

/** Forms and number input. */
var INPUT_NUMBERS = __assign({ fontSize: getSize(5), lineHeight: getLineHeight(5, 1.6) }, getTypographyForWeight("700"));
/** Forms and text fields. */
var INPUT_TEXT = __assign({ fontSize: getSize(0), lineHeight: getLineHeight(0, 1.7) }, getTypographyForWeight("500"));

var interactive = /*#__PURE__*/Object.freeze({
    __proto__: null,
    INPUT_NUMBERS: INPUT_NUMBERS,
    INPUT_TEXT: INPUT_TEXT
});

/** Main numbers. */
var NUMBER_1 = __assign(__assign({ fontSize: getSize(6), lineHeight: getLineHeight(6, 1.3) }, getTypographyForWeight("800")), { textTransform: "uppercase" });
/** Smaller numbers. */
var NUMBER_2 = __assign(__assign({ fontSize: getSize(4), lineHeight: getLineHeight(4, 1.3) }, getTypographyForWeight("800")), { textTransform: "uppercase" });
/** Small numbers. */
var NUMBER_3 = __assign(__assign({ fontSize: getSize(1), lineHeight: getLineHeight(1, 1.3) }, getTypographyForWeight("700")), { textTransform: "uppercase" });

var numbers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    NUMBER_1: NUMBER_1,
    NUMBER_2: NUMBER_2,
    NUMBER_3: NUMBER_3
});

var typography = __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, heading), overline), body), meta), button), interactive), numbers);

/** All components spacing. */
var space = {
    none: 0,
    xxsmall: 2,
    xsmall: 4,
    small: 8,
    medium: 12,
    large: 16,
    xlarge: 20,
    xxlarge: 24,
};

var components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    space: space
});

/** All layout spacing. */
var layout = {
    none: 0,
    xxsmall: 5,
    xsmall: 10,
    small: 12,
    medium: 16,
    large: 20,
    xlarge: 28,
    xxlarge: 32,
};

var layout$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    layout: layout
});

var spacing = __assign(__assign({}, layout$1), components);

var palette = {
    ElectricPurple_5: "#E4D2FD",
    ElectricPurple_7: "#DEC8FD", // Primary/LightPurple
    ElectricPurple_10: "#D2B6FC", // Primary/SoftPurple
    ElectricPurple_20: "#C097FB", // Primary/SoftPurple
    ElectricPurple_30: "#D2B6FC", // Primary/SoftPurple
    ElectricPurple_40: "#A063F8",
    ElectricPurple_50: "#8940F7", // Primary/Purple
    ElectricPurple_60: "#6E2DCF", // Primary/Purple
    ElectricPurple_70: "#551DA8", // Primary/Purple
    ElectricPurple_80: "#3D1180", // Primary/Purple
    ElectricPurple_90: "#280859",
    Green_30: "#66F487",
    Green_50: "#00C853",
    Green_70: "#009624",
    MidnightBlue_2_DS: "#F2F2F2", // Neutral/LightGrey
    MidnightBlue_3_S: "#F3EBFE", // Primary/LightPurple
    MidnightBlue_8: "#DADADA", // Neutral/SoftGrey
    MidnightBlue_10: "#DADADA", // Neutral/SoftGrey
    MidnightBlue_15: "#B0B0B0", // Neutral/MediumGrey
    MidnightBlue_25: "#B0B0B0", // Neutral/MediumGrey
    MidnightBlue_35: "#595959", // Neutral/WarmGrey
    MidnightBlue_45: "#595959", // Neutral/WarmGrey
    MidnightBlue_45_S: "#595959", // Neutral/WarmGrey
    MidnightBlue_60_S: "#1E1E1E", // Neutral/DarkGrey
    MidnightBlue_70: "#1E1E1E", // Neutral/DarkGrey
    MidnightBlue_80: "#150231", // Primary/DarkPurple
    MidnightBlue_85: "#150231", // Primary/DarkPurple
    MidnightBlue_85_S: "#150231", // Primary/DarkPurple
    MidnightBlue_100: "#0F0F0F", // Neutral/SoftBlack
    MidnightBlue_110: "#0F0F0F", // Neutral/SoftBlack
    Mint_30: "#58E4B1", // Secondary/Green/Green
    Mint_70: "#02B181", // Secondary/Green/Dark Green
    Neutral_0: "#FFFFFF", // Neutral/White
    Neutral_5: "#F6F5F7",
    Neutral_10: "#DBD9DE",
    Neutral_20: "#C2BFC6",
    Neutral_30: "#AAA6AF",
    Neutral_40: "#928E98",
    Neutral_50: "#7A7681",
    Neutral_55: "#736E7A",
    Neutral_60: "#605C67",
    Neutral_70: "#48444D",
    Neutral_80: "#302D34",
    Neutral_90: "#1E1D20",
    Neutral_100: "#0A090B",
    Pink_30: "#FF7961", // Alerts/Error Red/Light Red
    Pink_50: "#BA000D", // Alerts/Error Red/Dark Red
    Red_10: "#FFDDD6",
    Red_20: "#FF8D85",
    Red_30: "#FF6661",
    Red_50: "#FF7264",
    Red_70: "#C74139",
    Red_90: "#830710",
    SunsetPeach_10: "#FFA492", // Secondary/Red/Light Red
    SunsetPeach_30: "#FF7264", // Secondary/Red/Red
};

var light = {
    icons: palette.ElectricPurple_70,
    interactiveMain: palette.ElectricPurple_50,
    interactiveSecondary: palette.Neutral_40,
    interactiveText: palette.ElectricPurple_50,
    interactiveError: palette.Red_70,
    onInteractive: palette.Neutral_0,
    accent: palette.ElectricPurple_50,
    onAccent: palette.Neutral_0,
    surfacePrimary: palette.Neutral_0,
    surfaceVariant: palette.Neutral_10,
    surfacePromo: palette.ElectricPurple_5,
    surfaceError: palette.Red_10,
    onSurfacePrimary: palette.Neutral_90,
    onSurfaceSecondary: palette.Neutral_55,
    onSurfacePromoIcon: palette.ElectricPurple_7,
    backgroundPrimary: palette.Neutral_5,
    backgroundDialog: palette.Neutral_0,
    onBackgroundPrimary: palette.Neutral_90,
    onBackgroundSecondary: palette.Neutral_55,
    backgroundOnboarding: palette.MidnightBlue_80,
    success: palette.Green_70,
    confirmation: palette.Green_70,
    error: palette.Red_50,
    border: palette.Neutral_20,
    loading: "#ECECFF",
    brand: palette.ElectricPurple_50,
    brandSecondary: "#FFD5AF",
};
var dark = {
    icons: palette.ElectricPurple_20,
    interactiveMain: palette.ElectricPurple_50,
    interactiveSecondary: palette.Neutral_50,
    interactiveText: palette.ElectricPurple_40,
    interactiveError: palette.Red_20,
    onInteractive: palette.Neutral_0,
    accent: palette.ElectricPurple_40,
    onAccent: palette.Neutral_0,
    surfacePrimary: palette.Neutral_90,
    surfaceVariant: palette.Neutral_70,
    surfacePromo: palette.ElectricPurple_80,
    surfaceError: palette.Red_90,
    onSurfacePrimary: palette.Neutral_5,
    onSurfaceSecondary: palette.Neutral_40,
    onSurfacePromoIcon: palette.ElectricPurple_90,
    backgroundPrimary: palette.Neutral_100,
    backgroundDialog: palette.Neutral_80,
    onBackgroundPrimary: palette.Neutral_5,
    onBackgroundSecondary: palette.Neutral_40,
    backgroundOnboarding: palette.MidnightBlue_80,
    success: palette.Green_30,
    confirmation: palette.Green_30,
    error: palette.Red_30,
    border: palette.Neutral_70,
    loading: "#8B87AC",
    brand: palette.ElectricPurple_50,
    brandSecondary: "#FFD5AF",
};

var LIGHT_COLORS = __assign({ variant: "light" }, light);
var DARK_COLORS = __assign({ variant: "dark" }, dark);
var UIStyleContext = React.createContext({
    colors: LIGHT_COLORS,
    typography: typography,
    spacing: spacing,
});
var getColorsForVariant = function (variant, defaultValue) {
    var _a;
    return (_a = [LIGHT_COLORS, DARK_COLORS].find(function (colors) { return colors.variant === variant; })) !== null && _a !== void 0 ? _a : defaultValue;
};
var UIStyleContextProvider = function (_a) {
    var variant = _a.variant, children = _a.children;
    var style = useMemo(function () { return ({
        colors: getColorsForVariant(variant, DARK_COLORS),
        typography: typography,
        spacing: spacing,
    }); }, [variant]);
    return (React.createElement(UIStyleContext.Provider, { value: style }, children));
};
var useUIStyle = function () { return useContext(UIStyleContext); };

var Theme = __assign(__assign({}, Util), { ColorScheme: ColorScheme, FontFamily: FontFamily });

export { ColorPalette, Context, DARK_COLORS, LIGHT_COLORS, Theme, ThemeProvider, UIStyleContext, UIStyleContextProvider, dark, light, spacing, typography, useThemeContext, useUIStyle };
//# sourceMappingURL=index.js.map
